import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { buildWhatsAppUrl, sanitizeHtml } from "@/lib/content";
import BrandLogo from "@/components/BrandLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="blog-logo-link">
            <BrandLogo />
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-stone-600 hover:text-[#073D2D] transition-colors"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

// Blog Card Component
const BlogCard = ({ post, index }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <motion.div variants={fadeInUp}>
      <Link
        to={`/blog/${post.slug}`}
        className="blog-card group block"
        data-testid={`blog-post-card-${index}`}
      >
        <div className="h-56 overflow-hidden">
          <img
            src={
              post.featured_image_url ||
              "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=600"
            }
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-sm text-stone-500 mb-3">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(post.published_date)}</span>
          </div>
          <h2 className="font-heading text-xl font-semibold text-[#1C1917] mb-3 group-hover:text-[#073D2D] transition-colors line-clamp-2">
            {post.title}
          </h2>
          <div
            className="text-stone-600 text-sm line-clamp-3"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.excerpt) }}
          />
        </div>
      </Link>
    </motion.div>
  );
};

// Main Blog Page Component
export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const postsPerPage = 9;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: postsPerPage,
      });
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await axios.get(
        `${API_URL}/api/blog/posts?${params.toString()}`
      );
      setPosts(response.data.posts || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, postsPerPage]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPosts();
  };

  // Placeholder posts if API returns empty
  const placeholderPosts = [
    {
      id: 1,
      title: "Guide to Shopping at Balogun Market",
      excerpt:
        "Discover the best tips for navigating Lagos' largest fabric and fashion market. Learn about the best times to visit, how to negotiate, and where to find quality materials...",
      slug: "guide-balogun-market",
      published_date: "2024-12-15",
      featured_image_url:
        "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 2,
      title: "How to Source Quality Fabrics in Lagos",
      excerpt:
        "Learn how to identify authentic materials and get the best deals at Lagos fabric markets. This comprehensive guide covers everything from identifying genuine lace to checking fabric quality...",
      slug: "source-quality-fabrics",
      published_date: "2024-12-10",
      featured_image_url:
        "https://images.unsplash.com/photo-1746013189357-e17986c93fb2?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 3,
      title: "Bulk Buying Tips for Business Owners",
      excerpt:
        "Essential strategies for successful wholesale purchasing in Lagos markets. Whether you're a retailer or exporter, these tips will help you maximize your sourcing efficiency...",
      slug: "bulk-buying-tips",
      published_date: "2024-12-05",
      featured_image_url:
        "https://images.unsplash.com/photo-1722072391426-964abfef1924?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 4,
      title: "Understanding Market Pricing in Lagos",
      excerpt:
        "A deep dive into how prices work in Lagos markets. Learn about seasonal variations, bargaining culture, and how to ensure you're getting fair prices for your purchases...",
      slug: "market-pricing-lagos",
      published_date: "2024-11-30",
      featured_image_url:
        "https://images.unsplash.com/photo-1734255026082-82fdc81991f0?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 5,
      title: "Top 5 Markets for Electronics in Lagos",
      excerpt:
        "Looking for electronics? Here's your complete guide to the best markets for gadgets, appliances, and tech accessories in Lagos. Compare prices and quality across different markets...",
      slug: "electronics-markets-lagos",
      published_date: "2024-11-25",
      featured_image_url:
        "https://images.unsplash.com/photo-1746013189357-e17986c93fb2?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 6,
      title: "Food Sourcing Guide: Ebute Ero Market",
      excerpt:
        "Everything you need to know about sourcing quality foodstuff from Ebute Ero Market. From rice and beans to spices and dry goods, learn how to get the best quality at great prices...",
      slug: "ebute-ero-food-guide",
      published_date: "2024-11-20",
      featured_image_url:
        "https://images.unsplash.com/photo-1722072391426-964abfef1924?auto=format&fit=crop&q=80&w=600",
    },
  ];

  const displayPosts = posts.length > 0 ? posts : placeholderPosts;

  return (
    <div className="min-h-screen bg-[#fff7d6]" data-testid="blog-page">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-[#fffdf3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl md:text-5xl font-bold text-[#1C1917] mb-4"
              data-testid="blog-page-headline"
            >
              Market Insights & Tips
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-stone-600 mb-8"
            >
              Discover guides, tips, and stories from Lagos markets
            </motion.p>

            {/* Search Form */}
            <motion.form
              variants={fadeInUp}
              onSubmit={handleSearch}
              className="flex gap-3 max-w-md mx-auto"
              data-testid="blog-search-form"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-full"
                  data-testid="blog-search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full px-6"
                data-testid="blog-search-btn"
              >
                Search
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-xl overflow-hidden">
                  <Skeleton className="h-56 w-full" />
                  <div className="p-6">
                    <Skeleton className="h-4 w-24 mb-3" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-6 w-3/4 mb-3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayPosts.length > 0 ? (
            <>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {displayPosts.map((post, index) => (
                  <BlogCard key={post.id} post={post} index={index} />
                ))}
              </motion.div>

              {/* Pagination */}
              {posts.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="rounded-full"
                    data-testid="blog-prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-stone-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="rounded-full"
                    data-testid="blog-next-page-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-stone-600 text-lg">
                No blog posts found. Check back soon for new content!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp Float */}
      <a
        href={buildWhatsAppUrl(
          "Hello! I'm interested in your market sourcing services."
        )}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        data-testid="blog-whatsapp-float"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
